import { useState, type FormEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Lock, Mail, Pencil, Plus, ShieldCheck, Trash2, UserPlus, Users } from 'lucide-react';
import { PERMISSIONS, PERMISSION_GROUPS, type Permission } from '@garlic/shared';
import { api, errorMessage, fieldErrors, patch, post, put } from '../../lib/api';
import { relativeTime } from '../../lib/format';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, Field, Input, PageHeader, Select, Spinner, cx } from '../../ui/primitives';
import { Dialog, useToast } from '../../ui/feedback';

type Role = { id: string; name: string; key: string | null; permissions: string[]; isSystem: boolean; memberCount: number };
type Member = { id: string; status: 'ACTIVE' | 'BLOCKED'; createdAt: string; role: { id: string; name: string; key: string | null }; user: { id: string; name: string; email: string; pendingInvite: boolean }; lastSeenAt: string | null; isMe: boolean };
type Team = { maxStaff: number | null; members: Member[]; roles: Role[] };

export function StaffTeam({ permissions }: { permissions: Permission[] }) {
  const can = (p: Permission) => permissions.includes(p);
  const [tab, setTab] = useState<'members' | 'roles'>('members');
  const q = useQuery({ queryKey: ['staff-team'], queryFn: () => api<Team>('/api/staff/team') });
  return (
    <>
      <PageHeader title="Equipe" description="Quem acessa o sistema e o que cada função pode fazer. Todas as alterações ficam na auditoria." />
      <div role="tablist" className="mb-6 flex gap-1 border-b border-line">
        {([['members', 'Pessoas'], ['roles', 'Funções e permissões']] as const).map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className={cx('-mb-px border-b-2 px-3 py-2.5 text-sm font-medium', tab === k ? 'border-garlic-600 text-garlic-700' : 'border-transparent text-ink-3 hover:text-ink')}>{l}</button>
        ))}
      </div>
      {q.isPending && <Spinner />}
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {q.data && tab === 'members' && <Members team={q.data} canManage={can('staff.manage')} mine={permissions} />}
      {q.data && tab === 'roles' && <Roles team={q.data} canManage={can('roles.manage')} mine={permissions} />}
    </>
  );
}

/** Funções que eu posso atribuir: só as que não têm permissões que eu não tenho (mesma regra do servidor). */
const assignable = (roles: Role[], mine: Permission[]) => roles.filter((r) => r.permissions.every((p) => mine.includes(p as Permission)));

function Members({ team, canManage, mine }: { team: Team; canManage: boolean; mine: Permission[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [link, setLink] = useState<string>();
  const refresh = () => qc.invalidateQueries({ queryKey: ['staff-team'] });
  const run = async (fn: () => Promise<{ inviteUrl?: string } | unknown>, msg: string) => {
    try {
      const r = (await fn()) as { inviteUrl?: string } | undefined;
      if (r?.inviteUrl) setLink(r.inviteUrl);
      await refresh();
      toast.success(msg);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  return (
    <Card>
      <CardHeader
        title={`${team.members.length} pessoa(s)${team.maxStaff ? ` de ${team.maxStaff} no plano` : ''}`}
        action={canManage && <Button icon={<UserPlus className="size-4" />} onClick={() => setInviting(true)}>Convidar</Button>}
      />
      {link && <InviteLink url={link} />}
      {team.members.length === 0 ? (
        <EmptyState icon={<Users className="size-5" />} title="Ninguém na equipe ainda" />
      ) : (
        <ul className="divide-y divide-line">
          {team.members.map((m) => {
            const manageable = canManage && !m.isMe && assignable([{ ...m.role, permissions: team.roles.find((r) => r.id === m.role.id)?.permissions ?? [] } as Role], mine).length > 0;
            return (
              <li key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <div className="grid size-10 shrink-0 place-items-center rounded-full bg-paper-2 font-semibold text-ink-2">{m.user.name.slice(0, 1).toUpperCase()}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{m.user.name}{m.isMe && <span className="ml-2 text-xs text-ink-3">(você)</span>}</p>
                  <p className="truncate text-xs text-ink-3">{m.user.email}{m.lastSeenAt && ` · ativo ${relativeTime(m.lastSeenAt)}`}</p>
                </div>
                <Badge tone={m.role.key === 'admin' ? 'brand' : 'neutral'}>{m.role.name}</Badge>
                {m.status === 'BLOCKED' && <Badge tone="danger">Bloqueado</Badge>}
                {m.user.pendingInvite && <Badge tone="warning">Convite pendente</Badge>}
                {manageable && (
                  <div className="flex">
                    {m.user.pendingInvite && m.status === 'ACTIVE' && (
                      <Button size="sm" variant="ghost" aria-label="Reenviar convite" icon={<Mail className="size-4" />} onClick={() => run(() => post(`/api/staff/team/${m.id}/invite`), 'Convite reenviado')} />
                    )}
                    <Button size="sm" variant="ghost" aria-label={`Editar ${m.user.name}`} icon={<Pencil className="size-4" />} onClick={() => setEditing(m)} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {inviting && <InviteDialog roles={assignable(team.roles, mine)} onClose={() => setInviting(false)} onDone={(url) => { setLink(url); void refresh(); }} />}
      {editing && (
        <Dialog open onClose={() => setEditing(null)} title={editing.user.name}>
          <div className="space-y-4">
            <Field label="Função">
              {(id) => (
                <Select id={id} defaultValue={editing.role.id} onChange={(e) => run(() => patch(`/api/staff/team/${editing.id}`, { roleId: e.target.value }), 'Função alterada. A pessoa precisa entrar de novo.')}>
                  {assignable(team.roles, mine).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              )}
            </Field>
            <div className="flex flex-wrap gap-2 border-t border-line pt-4">
              <Button
                variant="secondary"
                icon={<Lock className="size-4" />}
                onClick={() => run(() => patch(`/api/staff/team/${editing.id}`, { status: editing.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE' }), editing.status === 'ACTIVE' ? 'Acesso bloqueado' : 'Acesso liberado').then(() => setEditing(null))}
              >
                {editing.status === 'ACTIVE' ? 'Bloquear acesso' : 'Desbloquear'}
              </Button>
              <Button
                variant="ghost"
                icon={<Trash2 className="size-4" />}
                onClick={() => confirm(`Remover ${editing.user.name} da equipe? O histórico de ações é mantido.`) && run(() => post(`/api/staff/team/${editing.id}/remove`), 'Removido da equipe').then(() => setEditing(null))}
              >
                Remover da equipe
              </Button>
            </div>
            <p className="text-xs text-ink-3">Bloquear ou mudar a função desconecta a pessoa na hora.</p>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

function InviteLink({ url }: { url: string }) {
  const toast = useToast();
  return (
    <div className="space-y-2 border-b border-line p-5">
      <Alert tone="warning">E-mail não configurado: envie este link para a pessoa por um canal seguro (vale 7 dias, uso único).</Alert>
      <div className="flex gap-2">
        <Input readOnly value={url} className="font-mono text-xs" aria-label="Link de convite" onFocus={(e) => e.currentTarget.select()} />
        <Button variant="secondary" icon={<Copy className="size-4" />} onClick={() => navigator.clipboard.writeText(url).then(() => toast.success('Link copiado'))}>Copiar</Button>
      </div>
    </div>
  );
}

function InviteDialog({ roles, onClose, onDone }: { roles: Role[]; onClose: () => void; onDone: (url?: string) => void }) {
  const toast = useToast();
  const [f, setF] = useState({ name: '', email: '', roleId: roles.find((r) => r.key === 'waiter')?.id ?? roles[0]?.id ?? '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await post<{ inviteUrl?: string; existingUser: boolean; inviteSentByEmail: boolean }>('/api/staff/team', f);
      toast.success(r.existingUser ? 'Acesso liberado: a pessoa entra com a senha que já usa no Garlic' : r.inviteSentByEmail ? 'Convite enviado por e-mail' : 'Convite criado');
      onDone(r.inviteUrl);
      onClose();
    } catch (err) {
      setErrors(fieldErrors(err));
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title="Convidar para a equipe" footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button type="submit" form="invite-form" loading={busy}>Convidar</Button></>}>
      <form id="invite-form" onSubmit={submit} className="space-y-4" noValidate>
        <Field label="Nome" error={errors.name}>{(id) => <Input id={id} autoFocus value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />}</Field>
        <Field label="E-mail" error={errors.email}>{(id) => <Input id={id} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />}</Field>
        <Field label="Função" error={errors.roleId}>
          {(id) => <Select id={id} value={f.roleId} onChange={(e) => setF({ ...f, roleId: e.target.value })}>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</Select>}
        </Field>
        <p className="text-xs text-ink-3">A pessoa recebe um link para criar a própria senha. Você só vê funções com permissões que você também tem.</p>
      </form>
    </Dialog>
  );
}

function Roles({ team, canManage, mine }: { team: Team; canManage: boolean; mine: Permission[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  return (
    <Card>
      <CardHeader title="Funções" description="Copie uma função padrão e ajuste. A função Administrador sempre tem todas as permissões." action={canManage && <Button icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>Nova função</Button>} />
      <ul className="divide-y divide-line">
        {team.roles.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
            {r.isSystem ? <ShieldCheck className="size-5 text-garlic-600" /> : <span className="size-5" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium">{r.name}</p>
              <p className="text-xs text-ink-3">{r.permissions.length} permissões · {r.memberCount} pessoa(s)</p>
            </div>
            {canManage && !r.isSystem && (
              <div className="flex">
                <Button size="sm" variant="ghost" icon={<Pencil className="size-4" />} onClick={() => setEditing(r)}>Editar</Button>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Remover ${r.name}`}
                  icon={<Trash2 className="size-4" />}
                  onClick={async () => {
                    if (!confirm(`Remover a função "${r.name}"?`)) return;
                    try {
                      await post(`/api/staff/roles/${r.id}/archive`);
                      await qc.invalidateQueries({ queryKey: ['staff-team'] });
                      toast.success('Função removida');
                    } catch (e) {
                      toast.error(errorMessage(e));
                    }
                  }}
                />
              </div>
            )}
            {canManage && !r.isSystem && (
              <Button size="sm" variant="ghost" onClick={() => setEditing({ ...r, id: '', name: `${r.name} (cópia)`, isSystem: false, key: null, memberCount: 0 })}>Copiar</Button>
            )}
          </li>
        ))}
      </ul>
      {editing && <RoleDialog value={editing} mine={mine} onClose={() => setEditing(null)} onSaved={() => qc.invalidateQueries({ queryKey: ['staff-team'] })} />}
    </Card>
  );
}

function RoleDialog({ value, mine, onClose, onSaved }: { value: Role | 'new'; mine: Permission[]; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const r = value === 'new' ? null : value;
  const [name, setName] = useState(r?.name ?? '');
  const [perms, setPerms] = useState<string[]>(r?.permissions ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const all = Object.keys(PERMISSIONS) as Permission[];
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      if (r?.id) await put(`/api/staff/roles/${r.id}`, { name, permissions: perms });
      else await post('/api/staff/roles', { name, permissions: perms });
      toast.success('Função salva');
      onSaved();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onClose={onClose} title={r?.id ? `Editar ${r.name}` : 'Nova função'} footer={<><Button variant="secondary" onClick={onClose}>Cancelar</Button><Button loading={busy} onClick={submit}>Salvar</Button></>}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <Field label="Nome da função">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
        {r?.id && <Alert tone="info">Quem usa esta função será desconectado ao salvar, para as novas permissões valerem na hora.</Alert>}
        <div className="max-h-[50dvh] space-y-4 overflow-y-auto pr-1">
          {PERMISSION_GROUPS.map((g) => {
            const list = all.filter((p) => g.prefix.some((pre) => p.startsWith(pre)));
            return (
              <fieldset key={g.label}>
                <legend className="mb-1.5 text-sm font-semibold">{g.label}</legend>
                <div className="space-y-1">
                  {list.map((p) => {
                    const allowed = mine.includes(p);
                    return (
                      <label key={p} className={cx('flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-paper', !allowed && 'opacity-50')} title={allowed ? undefined : 'Você não tem esta permissão para conceder'}>
                        <input type="checkbox" className="size-4 accent-garlic-600" disabled={!allowed} checked={perms.includes(p)} onChange={(e) => setPerms(e.target.checked ? [...perms, p] : perms.filter((x) => x !== p))} />
                        {PERMISSIONS[p]}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}
